import { assign, createActor, setup, type SnapshotFrom } from "xstate"

import {
	ORDER_COMMAND_TYPES,
	type Actor,
	type InternalRole,
	type OrderStatus,
	type PatientStepId,
	type QuestionnairePhase,
	type WorkflowTemplateId,
} from "../contracts"
import type { WorkflowTemplate } from "../templates/workflow-templates"

export const PATIENT_ORDER_WORKFLOW_MACHINE_ID = "patient-order-workflow" as const
export const PATIENT_ORDER_WORKFLOW_MACHINE_VERSION = 4 as const

export const PATIENT_WORKFLOW_STATE_VALUES = [
	"questionnaire",
	"account",
	"gender",
	"eligibility-screening",
	"post-auth",
	"shipping",
	"whats-next",
	"membership-agreement",
	"testosterone-agreement",
	"selection",
	"photo-id",
	"lab-results",
	"clinical-review-questionnaire",
	"lab-upload",
	"payment-ready",
	"lab-results-approved-payment-ready",
	"ELIGIBILITY_FAILED",
	"PAYMENT_PENDING",
	"PAYMENT_APPROVED",
	"PAYMENT_FAILED",
	"PAYMENT_RETRY_ALLOWED",
	"PAYMENT_RETRY_EXHAUSTED",
	"PAYMENT_REFUNDED",
	"LAB_REQUIRED",
	"LAB_ORDERED",
	"LAB_KIT_SHIPPED",
	"LAB_KIT_DELIVERED",
	"LAB_SAMPLE_IN_TRANSIT",
	"LAB_SAMPLE_DELIVERED",
	"LAB_SAMPLE_RECEIVED",
	"LAB_RESULTS_PENDING",
	"LAB_RESULTS_REJECTED",
	"LAB_RESULTS_APPROVED",
	"LAB_MANUAL_REVIEW_PENDING",
	"LAB_FAILED",
	"LAB_REORDER_REQUIRED",
	"clinical-review-completed",
	"VISIT_CONFIRMED",
	"VISIT_CANCELLED",
	"VISIT_COMPLETED",
	"PRESCRIPTION_APPROVED",
	"PRESCRIPTION_REJECTED",
	"PHARMACY_DISPATCHED",
	"PHARMACY_DELIVERED",
	"PHARMACY_FAILED",
	"REFILL_ELIGIBLE",
	"REFILL_UPCOMING",
	"REFILL_LOCKED",
	"REFILL_PAYMENT_PENDING",
	"REFILL_RELEASED",
	"REFILL_CANCELLED",
	"CORRECTION_REQUIRED",
	"CORRECTION_IN_PROGRESS",
	"CORRECTION_SUBMITTED",
	"CORRECTION_REJECTED",
	"CORRECTION_APPROVED",
	"CANCELLED",
	"EXPIRED",
	"COMPLETED",
] as const

export type PatientWorkflowStateValue = (typeof PATIENT_WORKFLOW_STATE_VALUES)[number]

export const PATIENT_WORKFLOW_TERMINAL_ORDER_STATUSES = [
	"PAYMENT_RETRY_EXHAUSTED",
	"REFILL_CANCELLED",
	"CANCELLED",
	"EXPIRED",
	"COMPLETED",
] as const satisfies readonly OrderStatus[]

export type LabFulfillmentMethod = "ship_kit" | "upload_existing_results"

export type PatientWorkflowContext = {
	readonly labFulfillmentMethod: LabFulfillmentMethod | null
	readonly kitLabUploadPayment?: "paid" | "unpaid" | undefined
}

const PATIENT_INTAKE_EVENT_TYPES = [
	"intake.updated",
	"continue_intake",
	"gender.selected",
	"profile.completed",
	"shipping.saved",
	"membership_agreement.accepted",
	"testosterone_agreement.accepted",
	"offering.selected",
	"photo_id.upload_recorded",
	"photo_id.completed",
	"lab.fulfillment_selected",
	"clinical_review.completed",
] as const

const LAB_REVIEW_EVENT_TYPES = ["lab.manual_review_requested", "lab.manual_review_rejected"] as const

export const GEN_HEALTH_WORKFLOW_EVENT_TYPES = [
	"visit.scheduled",
	"gen_health.visit_checked",
	"visit.rescheduled",
	"visit.cancelled",
	"visit.started",
	"visit.joined",
	"visit.completed",
	"chart_review.completed",
	"order.created",
	"order.status_updated",
	"gen_health.refill_ready",
	"gen_health.vendor_sync",
	"membership.renewal_paid",
	"prescription.sent",
	"prescription.status_updated",
	"prescription.tracking_updated",
	"membership.fulfillment_resumed",
	"membership.fulfillment_resume_payment_result",
] as const

export const PATIENT_WORKFLOW_EVENT_TYPES = [
	...ORDER_COMMAND_TYPES,
	...PATIENT_INTAKE_EVENT_TYPES,
	...LAB_REVIEW_EVENT_TYPES,
	"lab.switch_to_upload",
	...GEN_HEALTH_WORKFLOW_EVENT_TYPES,
] as const

export type PatientWorkflowEventType = (typeof PATIENT_WORKFLOW_EVENT_TYPES)[number]

type PatientWorkflowEventBase = {
	readonly payload?: Readonly<Record<string, unknown>>
}

export type PatientWorkflowEvent =
	| (PatientWorkflowEventBase & {
			readonly type: "lab.fulfillment_selected"
			readonly labFulfillmentMethod: LabFulfillmentMethod
	  })
	| (PatientWorkflowEventBase & {
			readonly type: "lab.switch_to_upload"
			readonly payment: "paid" | "unpaid"
	  })
	| (PatientWorkflowEventBase & {
			readonly type: Exclude<PatientWorkflowEventType, "lab.fulfillment_selected" | "lab.switch_to_upload">
	  })

export type PatientWorkflowCheckpoint = {
	readonly machineId: typeof PATIENT_ORDER_WORKFLOW_MACHINE_ID
	readonly machineVersion: typeof PATIENT_ORDER_WORKFLOW_MACHINE_VERSION
	readonly journeyDefinitionId: string
	readonly workflowTemplateId: WorkflowTemplateId
	readonly workflowTemplateVersion: number
	readonly questionnaireTemplateId: string
	readonly questionnaireTemplateVersion: string
	readonly value: PatientWorkflowStateValue
	readonly context: PatientWorkflowContext
	readonly revision: number
}

export type PatientWorkflowView = {
	readonly stateValue: PatientWorkflowStateValue
	readonly currentStatus: OrderStatus
	readonly canonicalStep: PatientStepId | null
	readonly accessibleSteps: readonly PatientStepId[]
	readonly previousEditableStep: PatientStepId | null
	readonly progressPercent: number
	readonly editable: boolean
	readonly allowedEvents: readonly PatientWorkflowEventType[]
	readonly revision: number
}

export type PatientWorkflowTransitionResult =
	| { readonly ok: true; readonly checkpoint: PatientWorkflowCheckpoint }
	| {
			readonly ok: false
			readonly error:
				| { readonly type: "unsupported_event"; readonly eventType: string }
				| {
						readonly type: "unauthorized_actor"
						readonly eventType: PatientWorkflowEventType
				  }
				| {
						readonly type: "invalid_revision"
						readonly expected: number
						readonly received: number
				  }
				| {
						readonly type: "invalid_transition"
						readonly fromState: PatientWorkflowStateValue
						readonly eventType: PatientWorkflowEventType
				  }
	  }

export class PatientWorkflowCheckpointError extends Error {
	readonly code: "invalid_checkpoint" | "machine_mismatch" | "template_mismatch" | "questionnaire_mismatch"

	constructor(code: PatientWorkflowCheckpointError["code"], message: string) {
		super(message)
		this.name = "PatientWorkflowCheckpointError"
		this.code = code
	}
}

export class PatientWorkflowTemplateError extends Error {
	constructor(message: string) {
		super(message)
		this.name = "PatientWorkflowTemplateError"
	}
}

type ActorPolicyName = "patient" | InternalRole

const PATIENT_EVENTS = [
	"create_order",
	"submit_questionnaire",
	"pass_eligibility_screening",
	"fail_eligibility",
	"reset_intake",
	"initiate_payment",
	"retry_payment",
	"payment.treatment_updated",
	"payment.shipping_updated",
	"submit_existing_lab_results",
	"fail_existing_lab_results",
	"start_correction",
	"submit_correction",
	"apply_correction",
	"book_visit",
	"reschedule_visit",
	"cancel_visit",
	"lab.manual_review_requested",
	"lab.switch_to_upload",
	...PATIENT_INTAKE_EVENT_TYPES,
] as const satisfies readonly PatientWorkflowEventType[]

const SUPPORT_EVENTS = [
	"request_correction",
	"cancel_order",
	"expire_order",
] as const satisfies readonly PatientWorkflowEventType[]

const OPS_EVENTS = [
	"lab.switch_to_upload",
	"submit_existing_lab_results",
	"fail_existing_lab_results",
	"lab.manual_review_requested",
	"approve_payment",
	"fail_payment",
	"allow_payment_retry",
	"exhaust_payment_retries",
	"refund_payment",
	"require_lab",
	"order_lab",
	"lab_kit_shipped",
	"lab_kit_delivered",
	"lab_sample_in_transit",
	"lab_sample_delivered",
	"lab_sample_received",
	"lab_results_pending",
	"approve_uploaded_lab_document",
	"lab.manual_review_rejected",
	"fail_lab",
	"require_lab_reorder",
	"dispatch_pharmacy",
	"deliver_pharmacy",
	"fail_pharmacy",
	"schedule_refill",
	"mark_refill_eligible",
	"lock_refill",
	"start_refill_payment",
	"release_refill",
	"cancel_refill",
	"expire_order",
	"complete_order",
	...GEN_HEALTH_WORKFLOW_EVENT_TYPES,
] as const satisfies readonly PatientWorkflowEventType[]

const CLINICIAN_EVENTS = [
	"book_visit",
	"reschedule_visit",
	"cancel_visit",
	"complete_visit",
	"approve_prescription",
	"reject_prescription",
	"approve_lab_results",
	"reject_lab_results",
	"approve_correction",
	"reject_correction",
] as const satisfies readonly PatientWorkflowEventType[]

function buildActorPolicy(): Readonly<Record<PatientWorkflowEventType, readonly ActorPolicyName[]>> {
	const patientEvents: ReadonlySet<PatientWorkflowEventType> = new Set(PATIENT_EVENTS)
	const supportEvents: ReadonlySet<PatientWorkflowEventType> = new Set(SUPPORT_EVENTS)
	const opsEvents: ReadonlySet<PatientWorkflowEventType> = new Set(OPS_EVENTS)
	const clinicianEvents: ReadonlySet<PatientWorkflowEventType> = new Set(CLINICIAN_EVENTS)
	const policy: Partial<Record<PatientWorkflowEventType, readonly ActorPolicyName[]>> = {}

	for (const eventType of PATIENT_WORKFLOW_EVENT_TYPES) {
		const actors: ActorPolicyName[] = []
		if (patientEvents.has(eventType)) actors.push("patient")
		if (supportEvents.has(eventType)) actors.push("support")
		if (opsEvents.has(eventType)) actors.push("ops")
		if (clinicianEvents.has(eventType)) actors.push("clinician")
		if (actors.some((actor) => actor !== "patient")) actors.push("admin")
		if (actors.length === 0) {
			throw new PatientWorkflowTemplateError(`No actor policy is declared for ${eventType}`)
		}
		policy[eventType] = actors
	}

	return policy as Readonly<Record<PatientWorkflowEventType, readonly ActorPolicyName[]>>
}

export const PATIENT_WORKFLOW_ACTOR_POLICY = buildActorPolicy()

const patientWorkflowEventTypeSet: ReadonlySet<string> = new Set(PATIENT_WORKFLOW_EVENT_TYPES)
const patientWorkflowStateValueSet: ReadonlySet<string> = new Set(PATIENT_WORKFLOW_STATE_VALUES)
const patientWorkflowVisitHistoryStates: ReadonlySet<PatientWorkflowStateValue> = new Set([
	"VISIT_CONFIRMED",
	"VISIT_CANCELLED",
	"VISIT_COMPLETED",
	"PRESCRIPTION_APPROVED",
	"PRESCRIPTION_REJECTED",
	"PHARMACY_DISPATCHED",
	"PHARMACY_DELIVERED",
	"PHARMACY_FAILED",
	"REFILL_ELIGIBLE",
	"REFILL_UPCOMING",
	"REFILL_LOCKED",
	"REFILL_PAYMENT_PENDING",
	"REFILL_RELEASED",
	"REFILL_CANCELLED",
	"CORRECTION_REQUIRED",
	"CORRECTION_IN_PROGRESS",
	"CORRECTION_SUBMITTED",
	"CORRECTION_REJECTED",
	"CORRECTION_APPROVED",
	"COMPLETED",
])

const patientWorkflowProductOrderReadyStates: ReadonlySet<PatientWorkflowStateValue> = new Set([
	"LAB_RESULTS_PENDING",
	"LAB_RESULTS_APPROVED",
	"LAB_MANUAL_REVIEW_PENDING",
	"lab-results-approved-payment-ready",
	"clinical-review-completed",
	"VISIT_CONFIRMED",
	"VISIT_CANCELLED",
	"VISIT_COMPLETED",
	"PRESCRIPTION_APPROVED",
	"PRESCRIPTION_REJECTED",
	"PHARMACY_DISPATCHED",
	"PHARMACY_DELIVERED",
	"PHARMACY_FAILED",
	"REFILL_ELIGIBLE",
	"REFILL_UPCOMING",
	"REFILL_LOCKED",
	"REFILL_PAYMENT_PENDING",
	"REFILL_RELEASED",
	"COMPLETED",
])

export function isPatientWorkflowEventType(value: string): value is PatientWorkflowEventType {
	return patientWorkflowEventTypeSet.has(value)
}

export function isPatientWorkflowStateValue(value: string): value is PatientWorkflowStateValue {
	return patientWorkflowStateValueSet.has(value)
}

export function hasPatientWorkflowVisitHistory(stateValue: PatientWorkflowStateValue): boolean {
	return patientWorkflowVisitHistoryStates.has(stateValue)
}

export function canActorSendPatientWorkflowEvent(actor: Actor, eventType: PatientWorkflowEventType): boolean {
	const allowedActors = PATIENT_WORKFLOW_ACTOR_POLICY[eventType]
	if (actor.type === "patient") return allowedActors.includes("patient")
	return allowedActors.includes(actor.role)
}

type WorkflowGuardName =
	| "selectedShipKitAfterLab"
	| "paymentBeforeLab"
	| "paymentAfterLab"
	| "shipKit"
	| "uploadedLabs"
	| "initialLabChoice"
	| "paidKitUpload"
	| "unpaidKitUpload"
	| "genHealthApplied"
	| "genHealthChartApproved"
	| "genHealthChartDenied"
	| "genHealthFailed"
	| "genHealthShipped"
	| "genHealthDelivered"

type WorkflowActionName = "selectLabFulfillment" | "clearLabFulfillment" | "switchToLabUpload" | "recordKitUploadPayment"

type TransitionDefinition = {
	readonly target: string
	readonly guard?: { readonly type: WorkflowGuardName }
	readonly actions?: { readonly type: WorkflowActionName }
}

type StateDefinition = {
	readonly type?: "final"
	readonly on?: Partial<Record<PatientWorkflowEventType, TransitionDefinition | readonly TransitionDefinition[]>>
	readonly always?: readonly TransitionDefinition[]
}

export type PatientWorkflowStateMetadata = {
	readonly currentStatus: OrderStatus
	readonly canonicalStep: PatientStepId | null
	readonly progressPercent: number
	readonly editable: boolean
}

const requiredQuestionnairePhases = ["pre_account", "hard_eligibility", "clinical_review"] as const

const configurableIntakeSteps = new Set<PatientStepId>([
	"questionnaire",
	"account",
	"gender",
	"eligibility-screening",
	"post-auth",
	"shipping",
	"whats-next",
	"membership-agreement",
	"testosterone-agreement",
	"selection",
	"photo-id",
	"lab-results",
	"clinical-review-questionnaire",
])

const requiredIntakeSteps = [
	"questionnaire",
	"account",
	"gender",
	"eligibility-screening",
	"post-auth",
	"lab-results",
] as const satisfies readonly PatientStepId[]

const completionEventByStep = {
	questionnaire: "submit_questionnaire",
	account: "create_order",
	gender: "gender.selected",
	"post-auth": "profile.completed",
	shipping: "shipping.saved",
	"whats-next": "continue_intake",
	"membership-agreement": "membership_agreement.accepted",
	"testosterone-agreement": "testosterone_agreement.accepted",
	selection: "offering.selected",
	"photo-id": "photo_id.completed",
	"lab-results": "lab.fulfillment_selected",
	"clinical-review-questionnaire": "clinical_review.completed",
} as const satisfies Partial<Record<PatientStepId, PatientWorkflowEventType>>

function getCompletionEvent(step: PatientStepId): PatientWorkflowEventType | undefined {
	switch (step) {
		case "questionnaire":
			return completionEventByStep.questionnaire
		case "account":
			return completionEventByStep.account
		case "gender":
			return completionEventByStep.gender
		case "post-auth":
			return completionEventByStep["post-auth"]
		case "shipping":
			return completionEventByStep.shipping
		case "whats-next":
			return completionEventByStep["whats-next"]
		case "membership-agreement":
			return completionEventByStep["membership-agreement"]
		case "testosterone-agreement":
			return completionEventByStep["testosterone-agreement"]
		case "selection":
			return completionEventByStep.selection
		case "photo-id":
			return completionEventByStep["photo-id"]
		case "lab-results":
			return completionEventByStep["lab-results"]
		case "clinical-review-questionnaire":
			return completionEventByStep["clinical-review-questionnaire"]
		case "eligibility-screening":
		case "eligibility-failed":
		case "payment":
		case "next-steps":
			return undefined
	}
}

function assertValidTemplate(
	workflowTemplate: WorkflowTemplate,
	questionnaireTemplate: PatientQuestionnaireTemplate,
): void {
	if (workflowTemplate.version < 1 || !Number.isInteger(workflowTemplate.version)) {
		throw new PatientWorkflowTemplateError("Workflow template version must be a positive integer")
	}
	const seenSteps = new Set<PatientStepId>()
	for (const step of workflowTemplate.intakeSteps) {
		if (!configurableIntakeSteps.has(step)) {
			throw new PatientWorkflowTemplateError(`${step} cannot be placed in intakeSteps`)
		}
		if (seenSteps.has(step)) {
			throw new PatientWorkflowTemplateError(`Workflow template repeats ${step}`)
		}
		seenSteps.add(step)
	}
	for (const requiredStep of requiredIntakeSteps) {
		if (!seenSteps.has(requiredStep)) {
			throw new PatientWorkflowTemplateError(`Workflow template is missing ${requiredStep}`)
		}
	}
	if (workflowTemplate.intakeSteps[0] !== "questionnaire") {
		throw new PatientWorkflowTemplateError("Workflow template must start with questionnaire")
	}
	if (workflowTemplate.intakeSteps[1] !== "account") {
		throw new PatientWorkflowTemplateError("Workflow template must place account after questionnaire")
	}
	const eligibilityIndex = workflowTemplate.intakeSteps.indexOf("eligibility-screening")
	for (const step of workflowTemplate.intakeSteps.slice(0, eligibilityIndex)) {
		if (projectedStatusForStep(step) === "ELIGIBILITY_PASSED") {
			throw new PatientWorkflowTemplateError(
				`Workflow template cannot place ${step} before eligibility-screening`,
			)
		}
	}
	if (questionnaireTemplate.id.trim().length === 0 || questionnaireTemplate.version.trim().length === 0) {
		throw new PatientWorkflowTemplateError("Questionnaire template ID and version are required")
	}
	const questionnairePhases = new Set(questionnaireTemplate.questions.map((question) => question.phase))
	for (const requiredPhase of requiredQuestionnairePhases) {
		if (!questionnairePhases.has(requiredPhase)) {
			throw new PatientWorkflowTemplateError(`Questionnaire template is missing ${requiredPhase}`)
		}
	}
}

function stateForStep(step: PatientStepId): PatientWorkflowStateValue {
	if (configurableIntakeSteps.has(step)) return step as PatientWorkflowStateValue
	throw new PatientWorkflowTemplateError(`${step} is not a configurable intake state`)
}

function projectedStatusForStep(step: PatientStepId): OrderStatus {
	if (step === "questionnaire" || step === "account") return "CREATED"
	if (step === "gender" || step === "eligibility-screening") return "QUESTIONNAIRE_SUBMITTED"
	return "ELIGIBILITY_PASSED"
}

function completesClinicalReviewDuringIntake(workflowTemplate: WorkflowTemplate): boolean {
	return workflowTemplate.intakeSteps.includes("clinical-review-questionnaire")
}

function makeTransition(target: string, guard?: WorkflowGuardName, action?: WorkflowActionName): TransitionDefinition {
	return {
		target,
		...(guard === undefined ? {} : { guard: { type: guard } }),
		...(action === undefined ? {} : { actions: { type: action } }),
	}
}

function makeGenHealthNoStateChangeTransitions(
	target: PatientWorkflowStateValue,
): Partial<Record<PatientWorkflowEventType, TransitionDefinition>> {
	return Object.fromEntries(
		GEN_HEALTH_WORKFLOW_EVENT_TYPES.map((eventType) => [eventType, makeTransition(target)]),
	) as Partial<Record<PatientWorkflowEventType, TransitionDefinition>>
}

function makePrescriptionTransitions(target: PatientWorkflowStateValue): Partial<Record<PatientWorkflowEventType, readonly TransitionDefinition[]>> {
	const transitions = [
		makeTransition("PHARMACY_FAILED", "genHealthFailed"),
		makeTransition("PHARMACY_DELIVERED", "genHealthDelivered"),
		...(target === "PHARMACY_DELIVERED" ? [] : [makeTransition("PHARMACY_DISPATCHED", "genHealthShipped")]),
		makeTransition(target),
	]
	return { "prescription.status_updated": transitions, "prescription.tracking_updated": transitions }
}

function makeAppliedGenHealthTransition(
	target: PatientWorkflowStateValue,
	fallback: PatientWorkflowStateValue,
): readonly TransitionDefinition[] {
	return [makeTransition(target, "genHealthApplied"), makeTransition(fallback)]
}

function makeGenHealthChartReviewTransitions(
	fallback: PatientWorkflowStateValue,
): readonly TransitionDefinition[] {
	return [
		makeTransition("PRESCRIPTION_APPROVED", "genHealthChartApproved"),
		makeTransition("PRESCRIPTION_REJECTED", "genHealthChartDenied"),
		makeTransition(fallback),
	]
}

function getWorkflowEventPayloadString(event: PatientWorkflowEvent, key: string): string | null {
	if (event.payload === undefined) return null
	const value = event.payload[key]
	return typeof value === "string" ? value : null
}

const PRE_PROVIDER_TERMINATION_TRANSITIONS = {
	cancel_order: makeTransition("CANCELLED"),
	expire_order: makeTransition("EXPIRED"),
} as const

function makeIntakeStates(workflowTemplate: WorkflowTemplate): {
	readonly states: Record<string, StateDefinition>
	readonly metadata: Partial<Record<PatientWorkflowStateValue, PatientWorkflowStateMetadata>>
} {
	const states: Record<string, StateDefinition> = {}
	const metadata: Partial<Record<PatientWorkflowStateValue, PatientWorkflowStateMetadata>> = {}
	const lastStepIndex = workflowTemplate.intakeSteps.length - 1

	for (const [index, step] of workflowTemplate.intakeSteps.entries()) {
		const stateValue = stateForStep(step)
		const nextStep = workflowTemplate.intakeSteps[index + 1]
		const nextTarget = nextStep === undefined ? "intake-complete" : stateForStep(nextStep)
		const progressPercent = Math.round((index / Math.max(lastStepIndex, 1)) * 65)
		metadata[stateValue] = {
			currentStatus: projectedStatusForStep(step),
			canonicalStep: step,
			progressPercent,
			editable: step !== "account",
		}
		const earlierStepTransitions = makeEditableStepTransitions(
			workflowTemplate,
			workflowTemplate.intakeSteps.slice(0, index).filter((earlierStep) => earlierStep !== "account"),
		)
		if (step === "account" && earlierStepTransitions.submit_questionnaire !== undefined) {
			earlierStepTransitions.submit_questionnaire = makeTransition("account", undefined, "clearLabFulfillment")
		}

		if (step === "eligibility-screening") {
			states[stateValue] = {
				on: {
					...earlierStepTransitions,
					"intake.updated": makeTransition(stateValue),
					pass_eligibility_screening: makeTransition(nextTarget),
					fail_eligibility: makeTransition("ELIGIBILITY_FAILED"),
					...PRE_PROVIDER_TERMINATION_TRANSITIONS,
				},
			}
			continue
		}

		const completionEvent = getCompletionEvent(step)
		if (completionEvent === undefined) {
			throw new PatientWorkflowTemplateError(`No completion event is declared for ${step}`)
		}
		const action = step === "lab-results" ? "selectLabFulfillment" : undefined
		states[stateValue] = {
			on: {
				...earlierStepTransitions,
				"intake.updated": makeTransition(stateValue),
				...(step === "photo-id" ? { "photo_id.upload_recorded": makeTransition(stateValue) } : {}),
				[completionEvent]: makeTransition(nextTarget, undefined, action),
				...PRE_PROVIDER_TERMINATION_TRANSITIONS,
			},
		}
	}

	return { states, metadata }
}

function getTargetAfterStep(workflowTemplate: WorkflowTemplate, step: PatientStepId): string {
	const stepIndex = workflowTemplate.intakeSteps.indexOf(step)
	if (stepIndex < 0) {
		throw new PatientWorkflowTemplateError(`Workflow template does not contain ${step}`)
	}
	const nextStep = workflowTemplate.intakeSteps[stepIndex + 1]
	return nextStep === undefined ? "intake-complete" : stateForStep(nextStep)
}

function getFirstPersistedIntakeState(workflowTemplate: WorkflowTemplate): PatientWorkflowStateValue {
	const accountIndex = workflowTemplate.intakeSteps.indexOf("account")
	const firstPersistedStep = workflowTemplate.intakeSteps[accountIndex + 1]
	if (firstPersistedStep === undefined) {
		throw new PatientWorkflowTemplateError("Workflow template requires a persisted step after account")
	}
	return stateForStep(firstPersistedStep)
}

function getEditableStepAction(
	workflowTemplate: WorkflowTemplate,
	step: PatientStepId,
): WorkflowActionName | undefined {
	if (step === "lab-results") return "selectLabFulfillment"
	const stepIndex = workflowTemplate.intakeSteps.indexOf(step)
	const labResultsIndex = workflowTemplate.intakeSteps.indexOf("lab-results")
	if (stepIndex < labResultsIndex) return "clearLabFulfillment"
	return undefined
}

function makeEditableStepTransitions(
	workflowTemplate: WorkflowTemplate,
	steps: readonly PatientStepId[],
	guard?: WorkflowGuardName,
): Partial<Record<PatientWorkflowEventType, TransitionDefinition>> {
	const transitions: Partial<Record<PatientWorkflowEventType, TransitionDefinition>> = {}
	for (const step of steps) {
		if (step === "account") continue
		if (step === "questionnaire") {
			transitions.submit_questionnaire = makeTransition(
				getFirstPersistedIntakeState(workflowTemplate),
				guard,
				"clearLabFulfillment",
			)
			continue
		}
		if (step === "eligibility-screening") {
			transitions.pass_eligibility_screening = makeTransition(
				getTargetAfterStep(workflowTemplate, step),
				guard,
				"clearLabFulfillment",
			)
			transitions.fail_eligibility = makeTransition("ELIGIBILITY_FAILED", guard, "clearLabFulfillment")
			continue
		}
		const eventType = getCompletionEvent(step)
		if (eventType === undefined) {
			throw new PatientWorkflowTemplateError(`No edit event is declared for ${step}`)
		}
		const action = getEditableStepAction(workflowTemplate, step)
		transitions[eventType] = makeTransition(getTargetAfterStep(workflowTemplate, step), guard, action)
	}
	return transitions
}

function makeEditablePaymentTransitions(
	workflowTemplate: WorkflowTemplate,
	guard?: WorkflowGuardName,
): Partial<Record<PatientWorkflowEventType, TransitionDefinition>> {
	return makeEditableStepTransitions(
		workflowTemplate,
		workflowTemplate.intakeSteps.filter((step) => step !== "account"),
		guard,
	)
}

function lifecycleMetadata(
	currentStatus: OrderStatus,
	canonicalStep: PatientStepId | null,
	progressPercent: number,
	editable = false,
): PatientWorkflowStateMetadata {
	return { currentStatus, canonicalStep, progressPercent, editable }
}

function getStateTransitions(state: StateDefinition): readonly TransitionDefinition[] {
	const eventTransitions = Object.values(state.on ?? {}).flatMap((transition) => {
		if (transition === undefined) return []
		return Array.isArray(transition) ? transition : [transition]
	})
	return [...eventTransitions, ...(state.always ?? [])]
}

function assertEveryNonFinalStateHasContinuation(states: Readonly<Record<string, StateDefinition>>): void {
	for (const [stateValue, state] of Object.entries(states)) {
		const transitions = getStateTransitions(state)
		if (state.type === "final") {
			if (transitions.length > 0) {
				throw new PatientWorkflowTemplateError(`Final state ${stateValue} cannot declare transitions`)
			}
			continue
		}
		if (transitions.length === 0) {
			throw new PatientWorkflowTemplateError(`Non-final state ${stateValue} has no legal continuation`)
		}
	}
}

function makeLifecycleStates(workflowTemplate: WorkflowTemplate): {
	readonly states: Record<string, StateDefinition>
	readonly metadata: Partial<Record<PatientWorkflowStateValue, PatientWorkflowStateMetadata>>
} {
	const clinicalReviewCompletedDuringIntake = completesClinicalReviewDuringIntake(workflowTemplate)
	let paymentApprovedTransition: TransitionDefinition | readonly TransitionDefinition[]
	if (!clinicalReviewCompletedDuringIntake) {
		paymentApprovedTransition = makeTransition("PAYMENT_APPROVED")
	} else if (workflowTemplate.policies.shipKitPaymentTiming === "after_lab_results") {
		paymentApprovedTransition = makeTransition("clinical-review-completed")
	} else {
		paymentApprovedTransition = [
			makeTransition("clinical-review-completed", "paymentAfterLab"),
			makeTransition("PAYMENT_APPROVED"),
		]
	}
	let labResultsApprovedTarget = "LAB_RESULTS_APPROVED"
	if (clinicalReviewCompletedDuringIntake) {
		labResultsApprovedTarget = "clinical-review-completed"
	}
	const editablePaymentTransitions = makeEditablePaymentTransitions(workflowTemplate, "paymentBeforeLab")
	const editablePreLabTransitions = makeEditablePaymentTransitions(workflowTemplate, "initialLabChoice")
	const uploadedLabAcceptedTransitions = [
		makeTransition(labResultsApprovedTarget, "paidKitUpload"),
		makeTransition("lab-results-approved-payment-ready", "uploadedLabs"),
	]
	const paymentFailureTransitions = {
		allow_payment_retry: makeTransition("PAYMENT_RETRY_ALLOWED"),
		exhaust_payment_retries: makeTransition("PAYMENT_RETRY_EXHAUSTED"),
	} as const
	const inlinePaymentEditTransitions =
		workflowTemplate.policies.shipKitPaymentTiming === "after_lab_results"
			? makeTransition("lab-results-approved-payment-ready")
			: [
					makeTransition("payment-ready", "paymentBeforeLab"),
					makeTransition("lab-results-approved-payment-ready", "paymentAfterLab"),
				]
	const labFailureTransitions = {
		require_lab_reorder: makeTransition("LAB_REORDER_REQUIRED", "shipKit"),
		submit_existing_lab_results: uploadedLabAcceptedTransitions,
		approve_uploaded_lab_document: makeTransition("LAB_RESULTS_PENDING", "uploadedLabs"),
	} as const

	const states: Record<string, StateDefinition> = {
		"intake-complete": {
			always: [
				makeTransition("payment-ready", "paymentBeforeLab"),
				makeTransition("LAB_REQUIRED", "selectedShipKitAfterLab"),
				makeTransition("lab-upload", "uploadedLabs"),
			],
		},
		"lab-upload": {
			on: {
				"intake.updated": makeTransition("lab-upload"),
				"lab.manual_review_requested": makeTransition("LAB_MANUAL_REVIEW_PENDING", "uploadedLabs"),
				submit_existing_lab_results: uploadedLabAcceptedTransitions,
				fail_existing_lab_results: makeTransition("LAB_FAILED", "uploadedLabs"),
				...editablePreLabTransitions,
				...PRE_PROVIDER_TERMINATION_TRANSITIONS,
			},
		},
		"payment-ready": {
			on: {
				"intake.updated": makeTransition("payment-ready"),
				initiate_payment: makeTransition("PAYMENT_PENDING"),
				...editablePaymentTransitions,
				...PRE_PROVIDER_TERMINATION_TRANSITIONS,
			},
		},
		ELIGIBILITY_FAILED: {
			on: {
				reset_intake: makeTransition("questionnaire", undefined, "clearLabFulfillment"),
				...PRE_PROVIDER_TERMINATION_TRANSITIONS,
			},
		},
		PAYMENT_PENDING: {
			on: {
				approve_payment: paymentApprovedTransition,
				fail_payment: makeTransition("PAYMENT_FAILED"),
				"payment.treatment_updated": inlinePaymentEditTransitions,
				"payment.shipping_updated": inlinePaymentEditTransitions,
			},
		},
		PAYMENT_FAILED: {
			on: {
				...paymentFailureTransitions,
				"payment.treatment_updated": inlinePaymentEditTransitions,
				"payment.shipping_updated": inlinePaymentEditTransitions,
			},
		},
		PAYMENT_RETRY_ALLOWED: {
			on: {
				retry_payment: makeTransition("PAYMENT_PENDING"),
				exhaust_payment_retries: makeTransition("PAYMENT_RETRY_EXHAUSTED"),
				"payment.treatment_updated": inlinePaymentEditTransitions,
				"payment.shipping_updated": inlinePaymentEditTransitions,
			},
		},
		PAYMENT_RETRY_EXHAUSTED: { type: "final" },
		PAYMENT_APPROVED: {
			on: {
				"intake.updated": makeTransition("PAYMENT_APPROVED"),
				refund_payment: makeTransition("PAYMENT_REFUNDED"),
				require_lab: makeTransition("LAB_REQUIRED", "paymentBeforeLab"),
				...(clinicalReviewCompletedDuringIntake
					? {}
					: {
							"clinical_review.completed": makeTransition(
								"clinical-review-completed",
								"paymentAfterLab",
							),
						}),
			},
		},
		PAYMENT_REFUNDED: {
			on: { refund_payment: makeTransition("PAYMENT_REFUNDED") },
		},
		LAB_REQUIRED: {
			on: {
				"intake.updated": makeTransition("LAB_REQUIRED"),
				order_lab: makeTransition("LAB_ORDERED", "shipKit"),
				...editablePreLabTransitions,
				...PRE_PROVIDER_TERMINATION_TRANSITIONS,
			},
		},
		LAB_ORDERED: {
			on: {
				lab_kit_shipped: makeTransition("LAB_KIT_SHIPPED"),
				fail_lab: makeTransition("LAB_FAILED"),
			},
		},
		LAB_KIT_SHIPPED: {
			on: {
				lab_kit_delivered: makeTransition("LAB_KIT_DELIVERED"),
				fail_lab: makeTransition("LAB_FAILED"),
			},
		},
		LAB_KIT_DELIVERED: {
			on: {
				lab_sample_in_transit: makeTransition("LAB_SAMPLE_IN_TRANSIT"),
				fail_lab: makeTransition("LAB_FAILED"),
			},
		},
		LAB_SAMPLE_IN_TRANSIT: {
			on: {
				lab_sample_delivered: makeTransition("LAB_SAMPLE_DELIVERED"),
				fail_lab: makeTransition("LAB_FAILED"),
			},
		},
		LAB_SAMPLE_DELIVERED: {
			on: {
				lab_sample_received: makeTransition("LAB_SAMPLE_RECEIVED"),
				fail_lab: makeTransition("LAB_FAILED"),
			},
		},
		LAB_SAMPLE_RECEIVED: {
			on: {
				lab_results_pending: makeTransition("LAB_RESULTS_PENDING"),
				fail_lab: makeTransition("LAB_FAILED"),
			},
		},
		LAB_RESULTS_PENDING: {
			on: {
				approve_lab_results: [
					makeTransition(labResultsApprovedTarget, "paymentBeforeLab"),
					makeTransition("lab-results-approved-payment-ready", "paymentAfterLab"),
				],
				reject_lab_results: makeTransition("LAB_RESULTS_REJECTED"),
				fail_lab: makeTransition("LAB_FAILED"),
			},
		},
		"lab-results-approved-payment-ready": {
			on: {
				initiate_payment: makeTransition("PAYMENT_PENDING", "paymentAfterLab"),
			},
		},
		LAB_RESULTS_REJECTED: { on: labFailureTransitions },
		LAB_RESULTS_APPROVED: {
			on: {
				"intake.updated": makeTransition("LAB_RESULTS_APPROVED"),
				...(clinicalReviewCompletedDuringIntake
					? {}
					: {
							"clinical_review.completed": makeTransition("clinical-review-completed"),
						}),
			},
		},
		LAB_MANUAL_REVIEW_PENDING: {
			on: {
				approve_uploaded_lab_document: uploadedLabAcceptedTransitions,
				"lab.manual_review_rejected": makeTransition("LAB_FAILED", "uploadedLabs"),
			},
		},
		LAB_FAILED: {
			on: {
				...labFailureTransitions,
				"lab.manual_review_requested": makeTransition("LAB_MANUAL_REVIEW_PENDING", "uploadedLabs"),
				fail_existing_lab_results: makeTransition("LAB_FAILED", "uploadedLabs"),
			},
		},
		LAB_REORDER_REQUIRED: {
			on: {
				order_lab: makeTransition("LAB_ORDERED", "shipKit"),
				submit_existing_lab_results: uploadedLabAcceptedTransitions,
			},
		},
		"clinical-review-completed": {
			on: {
				...makeGenHealthNoStateChangeTransitions("clinical-review-completed"),
				"visit.scheduled": makeAppliedGenHealthTransition("VISIT_CONFIRMED", "clinical-review-completed"),
				book_visit: makeTransition("VISIT_CONFIRMED"),
				...(clinicalReviewCompletedDuringIntake
					? { refund_payment: makeTransition("PAYMENT_REFUNDED", "paymentAfterLab") }
					: {}),
			},
		},
		VISIT_CONFIRMED: {
			on: {
				...makeGenHealthNoStateChangeTransitions("VISIT_CONFIRMED"),
				reschedule_visit: makeTransition("VISIT_CONFIRMED"),
				cancel_visit: makeTransition("VISIT_CANCELLED"),
				complete_visit: makeTransition("VISIT_COMPLETED"),
				"visit.cancelled": makeAppliedGenHealthTransition("VISIT_CANCELLED", "VISIT_CONFIRMED"),
				"visit.completed": makeAppliedGenHealthTransition("VISIT_COMPLETED", "VISIT_CONFIRMED"),
				"chart_review.completed": makeGenHealthChartReviewTransitions("VISIT_CONFIRMED"),
			},
		},
		VISIT_CANCELLED: {
			on: {
				...makeGenHealthNoStateChangeTransitions("VISIT_CANCELLED"),
				"visit.scheduled": makeAppliedGenHealthTransition("VISIT_CONFIRMED", "VISIT_CANCELLED"),
				book_visit: makeTransition("VISIT_CONFIRMED"),
				"visit.completed": makeAppliedGenHealthTransition("VISIT_COMPLETED", "VISIT_CANCELLED"),
				"chart_review.completed": makeGenHealthChartReviewTransitions("VISIT_CANCELLED"),
			},
		},
		VISIT_COMPLETED: {
			on: {
				...makeGenHealthNoStateChangeTransitions("VISIT_COMPLETED"),
				approve_prescription: makeTransition("PRESCRIPTION_APPROVED"),
				reject_prescription: makeTransition("PRESCRIPTION_REJECTED"),
				"chart_review.completed": makeGenHealthChartReviewTransitions("VISIT_COMPLETED"),
			},
		},
		PRESCRIPTION_REJECTED: {
			on: {
				...makeGenHealthNoStateChangeTransitions("PRESCRIPTION_REJECTED"),
				"visit.scheduled": makeAppliedGenHealthTransition("VISIT_CONFIRMED", "PRESCRIPTION_REJECTED"),
				book_visit: makeTransition("VISIT_CONFIRMED"),
				request_correction: makeTransition("CORRECTION_REQUIRED"),
			},
		},
		PRESCRIPTION_APPROVED: {
			on: {
				...makeGenHealthNoStateChangeTransitions("PRESCRIPTION_APPROVED"),
				...makePrescriptionTransitions("PRESCRIPTION_APPROVED"),
				fail_pharmacy: makeTransition("PHARMACY_FAILED"),
				dispatch_pharmacy: makeTransition("PHARMACY_DISPATCHED"),
			},
		},
		PHARMACY_DISPATCHED: {
			on: {
				...makeGenHealthNoStateChangeTransitions("PHARMACY_DISPATCHED"),
				"gen_health.refill_ready": makeAppliedGenHealthTransition("REFILL_RELEASED", "PHARMACY_DISPATCHED"),
				...makePrescriptionTransitions("PHARMACY_DISPATCHED"),
				schedule_refill: makeTransition("REFILL_UPCOMING"),
				mark_refill_eligible: makeTransition("REFILL_ELIGIBLE"),
				deliver_pharmacy: makeTransition("PHARMACY_DELIVERED"),
				fail_pharmacy: makeTransition("PHARMACY_FAILED"),
			},
		},
		PHARMACY_FAILED: {
			on: {
				...makeGenHealthNoStateChangeTransitions("PHARMACY_FAILED"),
				"gen_health.refill_ready": makeAppliedGenHealthTransition("REFILL_RELEASED", "PHARMACY_FAILED"),
				dispatch_pharmacy: makeTransition("PHARMACY_DISPATCHED"),
			},
		},
		PHARMACY_DELIVERED: {
			on: {
				...makeGenHealthNoStateChangeTransitions("PHARMACY_DELIVERED"),
				"gen_health.refill_ready": makeAppliedGenHealthTransition("REFILL_RELEASED", "PHARMACY_DELIVERED"),
				...makePrescriptionTransitions("PHARMACY_DELIVERED"),
				complete_order: makeTransition("COMPLETED"),
				schedule_refill: makeTransition("REFILL_UPCOMING"),
				mark_refill_eligible: makeTransition("REFILL_ELIGIBLE"),
			},
		},
		REFILL_UPCOMING: {
			on: {
				...makeGenHealthNoStateChangeTransitions("REFILL_UPCOMING"),
				"gen_health.refill_ready": makeAppliedGenHealthTransition("REFILL_RELEASED", "REFILL_UPCOMING"),
				mark_refill_eligible: makeTransition("REFILL_ELIGIBLE"),
				lock_refill: makeTransition("REFILL_LOCKED"),
			},
		},
		REFILL_ELIGIBLE: {
			on: {
				...makeGenHealthNoStateChangeTransitions("REFILL_ELIGIBLE"),
				"gen_health.refill_ready": makeAppliedGenHealthTransition("REFILL_RELEASED", "REFILL_ELIGIBLE"),
				start_refill_payment: makeTransition("REFILL_PAYMENT_PENDING"),
			},
		},
		REFILL_LOCKED: {
			on: {
				...makeGenHealthNoStateChangeTransitions("REFILL_LOCKED"),
				mark_refill_eligible: makeTransition("REFILL_ELIGIBLE"),
			},
		},
		REFILL_PAYMENT_PENDING: {
			on: {
				...makeGenHealthNoStateChangeTransitions("REFILL_PAYMENT_PENDING"),
				"gen_health.refill_ready": makeAppliedGenHealthTransition("REFILL_RELEASED", "REFILL_PAYMENT_PENDING"),
				release_refill: makeTransition("REFILL_RELEASED"),
				cancel_refill: makeTransition("REFILL_CANCELLED"),
			},
		},
		REFILL_RELEASED: {
			on: {
				...makeGenHealthNoStateChangeTransitions("REFILL_RELEASED"),
				...makePrescriptionTransitions("REFILL_RELEASED"),
				dispatch_pharmacy: makeTransition("PHARMACY_DISPATCHED"),
				deliver_pharmacy: makeTransition("PHARMACY_DELIVERED"),
				fail_pharmacy: makeTransition("PHARMACY_FAILED"),
			},
		},
		REFILL_CANCELLED: { on: makeGenHealthNoStateChangeTransitions("REFILL_CANCELLED") },
		CORRECTION_REQUIRED: {
			on: {
				...makeGenHealthNoStateChangeTransitions("CORRECTION_REQUIRED"),
				start_correction: makeTransition("CORRECTION_IN_PROGRESS"),
			},
		},
		CORRECTION_IN_PROGRESS: {
			on: {
				...makeGenHealthNoStateChangeTransitions("CORRECTION_IN_PROGRESS"),
				"intake.updated": makeTransition("CORRECTION_IN_PROGRESS"),
				submit_correction: makeTransition("CORRECTION_SUBMITTED"),
			},
		},
		CORRECTION_SUBMITTED: {
			on: {
				...makeGenHealthNoStateChangeTransitions("CORRECTION_SUBMITTED"),
				approve_correction: makeTransition("CORRECTION_APPROVED"),
				reject_correction: makeTransition("CORRECTION_REJECTED"),
			},
		},
		CORRECTION_REJECTED: {
			on: {
				...makeGenHealthNoStateChangeTransitions("CORRECTION_REJECTED"),
				start_correction: makeTransition("CORRECTION_IN_PROGRESS"),
			},
		},
		CORRECTION_APPROVED: {
			on: {
				...makeGenHealthNoStateChangeTransitions("CORRECTION_APPROVED"),
				apply_correction: makeTransition("VISIT_COMPLETED"),
			},
		},
		CANCELLED: { type: "final" },
		EXPIRED: { type: "final" },
		COMPLETED: { on: makeGenHealthNoStateChangeTransitions("COMPLETED") },
	}

	const metadata: Partial<Record<PatientWorkflowStateValue, PatientWorkflowStateMetadata>> = {
		"lab-upload": lifecycleMetadata("ELIGIBILITY_PASSED", "lab-results", 65, true),
		"payment-ready": lifecycleMetadata("ELIGIBILITY_PASSED", "payment", 70, true),
		"lab-results-approved-payment-ready": lifecycleMetadata("LAB_RESULTS_APPROVED", "payment", 85, true),
		ELIGIBILITY_FAILED: lifecycleMetadata("ELIGIBILITY_FAILED", "eligibility-failed", 20),
		PAYMENT_PENDING: lifecycleMetadata("PAYMENT_PENDING", "payment", 72),
		PAYMENT_FAILED: lifecycleMetadata("PAYMENT_FAILED", "payment", 72),
		PAYMENT_RETRY_ALLOWED: lifecycleMetadata("PAYMENT_RETRY_ALLOWED", "payment", 72),
		PAYMENT_RETRY_EXHAUSTED: lifecycleMetadata("PAYMENT_RETRY_EXHAUSTED", "next-steps", 72),
		PAYMENT_APPROVED: lifecycleMetadata("PAYMENT_APPROVED", "next-steps", 75),
		PAYMENT_REFUNDED: lifecycleMetadata("PAYMENT_REFUNDED", "next-steps", 75),
		LAB_REQUIRED: lifecycleMetadata("LAB_REQUIRED", "next-steps", 68, true),
		LAB_ORDERED: lifecycleMetadata("LAB_ORDERED", "next-steps", 70),
		LAB_KIT_SHIPPED: lifecycleMetadata("LAB_KIT_SHIPPED", "next-steps", 72),
		LAB_KIT_DELIVERED: lifecycleMetadata("LAB_KIT_DELIVERED", "next-steps", 74),
		LAB_SAMPLE_IN_TRANSIT: lifecycleMetadata("LAB_SAMPLE_IN_TRANSIT", "next-steps", 76),
		LAB_SAMPLE_DELIVERED: lifecycleMetadata("LAB_SAMPLE_DELIVERED", "next-steps", 78),
		LAB_SAMPLE_RECEIVED: lifecycleMetadata("LAB_SAMPLE_RECEIVED", "next-steps", 80),
		LAB_RESULTS_PENDING: lifecycleMetadata("LAB_RESULTS_PENDING", "next-steps", 82),
		LAB_RESULTS_REJECTED: lifecycleMetadata("LAB_RESULTS_REJECTED", "next-steps", 82),
		LAB_RESULTS_APPROVED: lifecycleMetadata("LAB_RESULTS_APPROVED", "clinical-review-questionnaire", 85),
		LAB_MANUAL_REVIEW_PENDING: lifecycleMetadata("LAB_RESULTS_PENDING", "next-steps", 82),
		LAB_FAILED: lifecycleMetadata("LAB_FAILED", "lab-results", 70),
		LAB_REORDER_REQUIRED: lifecycleMetadata("LAB_REORDER_REQUIRED", "next-steps", 70),
		"clinical-review-completed": lifecycleMetadata("LAB_RESULTS_APPROVED", "next-steps", 88),
		VISIT_CONFIRMED: lifecycleMetadata("VISIT_CONFIRMED", "next-steps", 90),
		VISIT_CANCELLED: lifecycleMetadata("VISIT_CANCELLED", "next-steps", 88),
		VISIT_COMPLETED: lifecycleMetadata("VISIT_COMPLETED", "next-steps", 92),
		PRESCRIPTION_APPROVED: lifecycleMetadata("PRESCRIPTION_APPROVED", "next-steps", 95),
		PRESCRIPTION_REJECTED: lifecycleMetadata("PRESCRIPTION_REJECTED", "next-steps", 93),
		PHARMACY_DISPATCHED: lifecycleMetadata("PHARMACY_DISPATCHED", "next-steps", 97),
		PHARMACY_DELIVERED: lifecycleMetadata("PHARMACY_DELIVERED", "next-steps", 99),
		PHARMACY_FAILED: lifecycleMetadata("PHARMACY_FAILED", "next-steps", 96),
		REFILL_ELIGIBLE: lifecycleMetadata("REFILL_ELIGIBLE", "next-steps", 95),
		REFILL_UPCOMING: lifecycleMetadata("REFILL_UPCOMING", "next-steps", 95),
		REFILL_LOCKED: lifecycleMetadata("REFILL_LOCKED", "next-steps", 95),
		REFILL_PAYMENT_PENDING: lifecycleMetadata("REFILL_PAYMENT_PENDING", "next-steps", 96),
		REFILL_RELEASED: lifecycleMetadata("REFILL_RELEASED", "next-steps", 97),
		REFILL_CANCELLED: lifecycleMetadata("REFILL_CANCELLED", "next-steps", 95),
		CORRECTION_REQUIRED: lifecycleMetadata("CORRECTION_REQUIRED", "next-steps", 50),
		CORRECTION_IN_PROGRESS: lifecycleMetadata("CORRECTION_IN_PROGRESS", "questionnaire", 45),
		CORRECTION_SUBMITTED: lifecycleMetadata("CORRECTION_SUBMITTED", "next-steps", 50),
		CORRECTION_REJECTED: lifecycleMetadata("CORRECTION_REJECTED", "questionnaire", 45),
		CORRECTION_APPROVED: lifecycleMetadata("CORRECTION_APPROVED", "next-steps", 50),
		CANCELLED: lifecycleMetadata("CANCELLED", null, 100),
		EXPIRED: lifecycleMetadata("EXPIRED", null, 100),
		COMPLETED: lifecycleMetadata("COMPLETED", null, 100),
	}
	if (workflowTemplate.policies.shipKitPaymentTiming === "after_lab_results") {
		states["intake-complete"] = {
			always: [makeTransition("LAB_REQUIRED", "selectedShipKitAfterLab"), makeTransition("lab-upload", "uploadedLabs")],
		}
		delete states["payment-ready"]
		delete metadata["payment-ready"]
		states.LAB_RESULTS_PENDING = {
			on: {
				approve_lab_results: makeTransition("lab-results-approved-payment-ready", "paymentAfterLab"),
				reject_lab_results: makeTransition("LAB_RESULTS_REJECTED"),
				fail_lab: makeTransition("LAB_FAILED"),
			},
		}
	} else {
		states["intake-complete"] = {
			always: [makeTransition("payment-ready", "paymentBeforeLab"), makeTransition("lab-upload", "uploadedLabs")],
		}
	}
	if (clinicalReviewCompletedDuringIntake) {
		if (workflowTemplate.policies.shipKitPaymentTiming === "before_lab_order") {
			delete states.LAB_RESULTS_APPROVED
			delete metadata.LAB_RESULTS_APPROVED
		} else {
			delete states.PAYMENT_APPROVED
			delete metadata.PAYMENT_APPROVED
		}
	}
	for (const stateValue of [
		"payment-ready", "PAYMENT_PENDING", "PAYMENT_FAILED", "PAYMENT_RETRY_ALLOWED",
		"LAB_REQUIRED", "LAB_ORDERED", "LAB_KIT_SHIPPED", "LAB_KIT_DELIVERED",
		"LAB_SAMPLE_IN_TRANSIT", "LAB_SAMPLE_DELIVERED", "LAB_SAMPLE_RECEIVED",
		"LAB_RESULTS_PENDING", "LAB_RESULTS_REJECTED", "LAB_FAILED", "LAB_REORDER_REQUIRED",
		...(workflowTemplate.policies.shipKitPaymentTiming === "before_lab_order" ? ["PAYMENT_APPROVED"] : []),
	]) {
		const state = states[stateValue]
		if (state !== undefined) {
			states[stateValue] = {
				...state,
				on: { ...state.on, "lab.switch_to_upload": makeTransition("lab-upload", "shipKit", "switchToLabUpload") },
			}
		}
	}
	for (const stateValue of ["lab-upload", "LAB_MANUAL_REVIEW_PENDING", "LAB_FAILED", "LAB_RESULTS_REJECTED"]) {
		const state = states[stateValue]
		if (state === undefined) throw new PatientWorkflowTemplateError(`Missing upload state ${stateValue}`)
		states[stateValue] = {
			...state,
			on: {
				...state.on,
				approve_payment: makeTransition(stateValue, "unpaidKitUpload", "recordKitUploadPayment"),
				fail_payment: makeTransition(stateValue, "unpaidKitUpload"),
			},
		}
	}
	states["lab-results-approved-payment-ready"] = {
		on: {
			...states["lab-results-approved-payment-ready"]?.on,
			approve_payment: makeTransition(labResultsApprovedTarget, "unpaidKitUpload", "recordKitUploadPayment"),
			fail_payment: makeTransition("lab-results-approved-payment-ready", "unpaidKitUpload"),
		},
	}
	const pendingPaymentState = states.PAYMENT_PENDING
	if (pendingPaymentState === undefined) throw new PatientWorkflowTemplateError("Missing payment pending state")
	states.PAYMENT_PENDING = {
		...pendingPaymentState,
		on: {
			...pendingPaymentState.on,
			approve_payment: [makeTransition(labResultsApprovedTarget, "unpaidKitUpload", "recordKitUploadPayment"),
				...(Array.isArray(paymentApprovedTransition) ? paymentApprovedTransition : [paymentApprovedTransition])],
		},
	}

	return { states, metadata }
}

function isPaymentBeforeLab(workflowTemplate: WorkflowTemplate, context: PatientWorkflowContext): boolean {
	return (
		context.labFulfillmentMethod === "ship_kit" && workflowTemplate.policies.shipKitPaymentTiming === "before_lab_order"
	)
}

function isPaymentAfterLab(workflowTemplate: WorkflowTemplate, context: PatientWorkflowContext): boolean {
	if (context.labFulfillmentMethod === "upload_existing_results") return true
	return (
		context.labFulfillmentMethod === "ship_kit" &&
		workflowTemplate.policies.shipKitPaymentTiming === "after_lab_results"
	)
}

function createConfiguredMachine(workflowTemplate: WorkflowTemplate, states: Record<string, StateDefinition>) {
	return setup({
		types: {} as {
			context: PatientWorkflowContext
			events: PatientWorkflowEvent
		},
		actions: {
			recordKitUploadPayment: assign(() => ({ kitLabUploadPayment: "paid" as const })),
			switchToLabUpload: assign(({ event }) => {
				if (event.type !== "lab.switch_to_upload") {
					throw new PatientWorkflowTemplateError("Lab switch action received the wrong event")
				}
				return { labFulfillmentMethod: "upload_existing_results" as const, kitLabUploadPayment: event.payment }
			}),
			selectLabFulfillment: assign(({ event }) => {
				if (event.type !== "lab.fulfillment_selected") {
					throw new PatientWorkflowTemplateError("Lab selection action received the wrong event")
				}
				return { labFulfillmentMethod: event.labFulfillmentMethod }
			}),
			clearLabFulfillment: assign(() => ({ labFulfillmentMethod: null })),
		},
		guards: {
			unpaidKitUpload: ({ context }) => context.labFulfillmentMethod === "upload_existing_results" && context.kitLabUploadPayment === "unpaid",
			initialLabChoice: ({ context }) => context.kitLabUploadPayment === undefined,
			paidKitUpload: ({ context }) => context.labFulfillmentMethod === "upload_existing_results" && context.kitLabUploadPayment === "paid",
			selectedShipKitAfterLab: ({ context }) =>
				context.labFulfillmentMethod === "ship_kit" &&
				workflowTemplate.policies.shipKitPaymentTiming === "after_lab_results",
			paymentBeforeLab: ({ context }) => isPaymentBeforeLab(workflowTemplate, context),
			paymentAfterLab: ({ context }) => isPaymentAfterLab(workflowTemplate, context),
			shipKit: ({ context }) => context.labFulfillmentMethod === "ship_kit",
			uploadedLabs: ({ context }) => context.labFulfillmentMethod === "upload_existing_results",
			genHealthFailed: ({ event }) => event.payload?.["activeFill"] === true && getWorkflowEventPayloadString(event, "disposition") === "applied" && getWorkflowEventPayloadString(event, "signal") === "failed",
			genHealthShipped: ({ event }) => event.payload?.["activeFill"] === true && getWorkflowEventPayloadString(event, "disposition") === "applied" && getWorkflowEventPayloadString(event, "signal") === "shipped",
			genHealthDelivered: ({ event }) => event.payload?.["activeFill"] === true && getWorkflowEventPayloadString(event, "disposition") === "applied" && getWorkflowEventPayloadString(event, "signal") === "delivered",
			genHealthApplied: ({ event }) => getWorkflowEventPayloadString(event, "disposition") === "applied",
			genHealthChartApproved: ({ event }) =>
				getWorkflowEventPayloadString(event, "disposition") === "applied" &&
				getWorkflowEventPayloadString(event, "decision") === "approved",
			genHealthChartDenied: ({ event }) =>
				getWorkflowEventPayloadString(event, "disposition") === "applied" &&
				getWorkflowEventPayloadString(event, "decision") === "denied",
		},
	}).createMachine({
		id: PATIENT_ORDER_WORKFLOW_MACHINE_ID,
		context: { labFulfillmentMethod: null },
		initial: "questionnaire",
		states,
	})
}

export type PatientOrderMachine = {
	readonly machine: ReturnType<typeof createConfiguredMachine>
	readonly workflowTemplate: WorkflowTemplate
	readonly questionnaireTemplate: Pick<PatientQuestionnaireTemplate, "id" | "version">
	readonly stateMetadata: Readonly<Partial<Record<PatientWorkflowStateValue, PatientWorkflowStateMetadata>>>
	readonly stateValues: ReadonlySet<PatientWorkflowStateValue>
}

export function createPatientOrderMachine(args: {
	readonly workflowTemplate: WorkflowTemplate
	readonly questionnaireTemplate: PatientQuestionnaireTemplate
}): PatientOrderMachine {
	assertValidTemplate(args.workflowTemplate, args.questionnaireTemplate)
	const intake = makeIntakeStates(args.workflowTemplate)
	const lifecycle = makeLifecycleStates(args.workflowTemplate)
	const states = { ...intake.states, ...lifecycle.states }
	assertEveryNonFinalStateHasContinuation(states)
	const stateMetadata = { ...intake.metadata, ...lifecycle.metadata }
	const stateValues = new Set<PatientWorkflowStateValue>()

	for (const stateValue of Object.keys(states)) {
		if (!isPatientWorkflowStateValue(stateValue)) {
			if (stateValue === "intake-complete") continue
			throw new PatientWorkflowTemplateError(`Machine contains unregistered state ${stateValue}`)
		}
		const metadata = stateMetadata[stateValue]
		if (metadata === undefined) {
			throw new PatientWorkflowTemplateError(`State ${stateValue} has no workflow metadata`)
		}
		stateValues.add(stateValue)
	}

	return {
		machine: createConfiguredMachine(args.workflowTemplate, states),
		workflowTemplate: args.workflowTemplate,
		questionnaireTemplate: {
			id: args.questionnaireTemplate.id,
			version: args.questionnaireTemplate.version,
		},
		stateMetadata,
		stateValues,
	}
}

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
	return typeof value === "object" && value !== null && !Array.isArray(value)
}

function assertNonNegativeInteger(value: unknown, fieldName: string): number {
	if (typeof value !== "number" || !Number.isInteger(value) || value < 0) {
		throw new PatientWorkflowCheckpointError("invalid_checkpoint", `${fieldName} must be a non-negative integer`)
	}
	return value
}

function parseCheckpointContext(value: unknown): PatientWorkflowContext {
	if (!isRecord(value)) {
		throw new PatientWorkflowCheckpointError("invalid_checkpoint", "Workflow context must be an object")
	}
	const labFulfillmentMethod = value.labFulfillmentMethod
	if (
		labFulfillmentMethod !== null &&
		labFulfillmentMethod !== "ship_kit" &&
		labFulfillmentMethod !== "upload_existing_results"
	) {
		throw new PatientWorkflowCheckpointError(
			"invalid_checkpoint",
			"Workflow context has an invalid lab fulfillment method",
		)
	}
	const kitLabUploadPayment = value.kitLabUploadPayment
	if (kitLabUploadPayment !== undefined && kitLabUploadPayment !== "paid" && kitLabUploadPayment !== "unpaid") {
		throw new PatientWorkflowCheckpointError("invalid_checkpoint", "Workflow context has an invalid kit upload payment")
	}
	if (kitLabUploadPayment !== undefined && labFulfillmentMethod !== "upload_existing_results") {
		throw new PatientWorkflowCheckpointError("invalid_checkpoint", "Kit upload recovery requires uploaded labs")
	}
	return { labFulfillmentMethod, ...(kitLabUploadPayment === undefined ? {} : { kitLabUploadPayment }) }
}

function getActorStateValue(value: unknown): PatientWorkflowStateValue {
	if (typeof value !== "string" || !isPatientWorkflowStateValue(value)) {
		throw new PatientWorkflowCheckpointError("invalid_checkpoint", "Workflow state value is invalid")
	}
	return value
}

function assertStateContextInvariant(
	machine: PatientOrderMachine,
	value: PatientWorkflowStateValue,
	context: PatientWorkflowContext,
): void {
	const labMethodIndependentStates: ReadonlySet<PatientWorkflowStateValue> = new Set([
		"ELIGIBILITY_FAILED",
		"CANCELLED",
		"EXPIRED",
	])
	const intakeStepIndex = machine.workflowTemplate.intakeSteps.findIndex((step) => step === value)
	const labResultsStepIndex = machine.workflowTemplate.intakeSteps.indexOf("lab-results")
	const routeCanPrecedeLabSelection = intakeStepIndex >= 0 && intakeStepIndex <= labResultsStepIndex
	const labMethodMayBeMissing = routeCanPrecedeLabSelection || labMethodIndependentStates.has(value)
	if (!labMethodMayBeMissing && context.labFulfillmentMethod === null) {
		throw new PatientWorkflowCheckpointError(
			"invalid_checkpoint",
			`Workflow state ${value} requires a lab fulfillment method`,
		)
	}
	if (value === "lab-upload" && context.labFulfillmentMethod !== "upload_existing_results") {
		throw new PatientWorkflowCheckpointError("invalid_checkpoint", "Lab upload state requires uploaded labs")
	}
	const shipKitOnlyStates = new Set<PatientWorkflowStateValue>([
		"LAB_REQUIRED",
		"LAB_ORDERED",
		"LAB_KIT_SHIPPED",
		"LAB_KIT_DELIVERED",
		"LAB_SAMPLE_IN_TRANSIT",
		"LAB_SAMPLE_DELIVERED",
		"LAB_SAMPLE_RECEIVED",
		"LAB_REORDER_REQUIRED",
	])
	if (shipKitOnlyStates.has(value) && context.labFulfillmentMethod !== "ship_kit") {
		throw new PatientWorkflowCheckpointError("invalid_checkpoint", `${value} requires a ship-kit workflow`)
	}
	if (value === "payment-ready" && !isPaymentBeforeLab(machine.workflowTemplate, context)) {
		throw new PatientWorkflowCheckpointError(
			"invalid_checkpoint",
			"Payment-ready state requires payment before lab ordering",
		)
	}
	if (value === "lab-results-approved-payment-ready" && !isPaymentAfterLab(machine.workflowTemplate, context)) {
		throw new PatientWorkflowCheckpointError(
			"invalid_checkpoint",
			"Post-results payment state requires payment after lab results",
		)
	}
}

function checkpointFromState(args: {
	readonly machine: PatientOrderMachine
	readonly journeyDefinitionId: string
	readonly value: PatientWorkflowStateValue
	readonly context: PatientWorkflowContext
	readonly revision: number
}): PatientWorkflowCheckpoint {
	if (args.journeyDefinitionId.trim().length === 0) {
		throw new PatientWorkflowCheckpointError("invalid_checkpoint", "Journey definition ID is required")
	}
	assertStateContextInvariant(args.machine, args.value, args.context)
	return {
		machineId: PATIENT_ORDER_WORKFLOW_MACHINE_ID,
		machineVersion: PATIENT_ORDER_WORKFLOW_MACHINE_VERSION,
		journeyDefinitionId: args.journeyDefinitionId,
		workflowTemplateId: args.machine.workflowTemplate.id,
		workflowTemplateVersion: args.machine.workflowTemplate.version,
		questionnaireTemplateId: args.machine.questionnaireTemplate.id,
		questionnaireTemplateVersion: args.machine.questionnaireTemplate.version,
		value: args.value,
		context: args.context,
		revision: assertNonNegativeInteger(args.revision, "revision"),
	}
}

export function createPatientWorkflowCheckpoint(
	machine: PatientOrderMachine,
	args: { readonly journeyDefinitionId: string; readonly revision: number },
): PatientWorkflowCheckpoint {
	return checkpointFromState({
		machine,
		journeyDefinitionId: args.journeyDefinitionId,
		value: "questionnaire",
		context: { labFulfillmentMethod: null },
		revision: args.revision,
	})
}

export function createPatientWorkflowCheckpointFromSnapshot(
	machine: PatientOrderMachine,
	args: {
		readonly journeyDefinitionId: string
		readonly snapshot: SnapshotFrom<PatientOrderMachine["machine"]>
		readonly revision: number
	},
): PatientWorkflowCheckpoint {
	return checkpointFromState({
		machine,
		journeyDefinitionId: args.journeyDefinitionId,
		value: getActorStateValue(args.snapshot.value),
		context: parseCheckpointContext(args.snapshot.context),
		revision: args.revision,
	})
}

export function createPersistedPatientWorkflowCheckpoint(
	machine: PatientOrderMachine,
	args: { readonly journeyDefinitionId: string; readonly revision: number },
): PatientWorkflowCheckpoint {
	const actor = createActor(machine.machine)
	actor.start()
	actor.send({ type: "submit_questionnaire" })
	const accountSnapshot = actor.getSnapshot()
	if (accountSnapshot.value !== "account") {
		actor.stop()
		throw new PatientWorkflowTemplateError("Questionnaire completion did not reach account")
	}
	actor.send({ type: "create_order" })
	const postAccountSnapshot = actor.getSnapshot()
	const value = getActorStateValue(postAccountSnapshot.value)
	const context = postAccountSnapshot.context
	actor.stop()
	return checkpointFromState({
		machine,
		journeyDefinitionId: args.journeyDefinitionId,
		value,
		context,
		revision: args.revision,
	})
}

export function restorePatientWorkflowCheckpoint(
	machine: PatientOrderMachine,
	value: unknown,
): PatientWorkflowCheckpoint {
	if (!isRecord(value)) {
		throw new PatientWorkflowCheckpointError("invalid_checkpoint", "Workflow checkpoint must be an object")
	}
	if (
		value.machineId !== PATIENT_ORDER_WORKFLOW_MACHINE_ID ||
		value.machineVersion !== PATIENT_ORDER_WORKFLOW_MACHINE_VERSION
	) {
		throw new PatientWorkflowCheckpointError("machine_mismatch", "Workflow machine identity or version does not match")
	}
	if (
		value.workflowTemplateId !== machine.workflowTemplate.id ||
		value.workflowTemplateVersion !== machine.workflowTemplate.version
	) {
		throw new PatientWorkflowCheckpointError(
			"template_mismatch",
			"Workflow template identity or version does not match",
		)
	}
	if (
		value.questionnaireTemplateId !== machine.questionnaireTemplate.id ||
		value.questionnaireTemplateVersion !== machine.questionnaireTemplate.version
	) {
		throw new PatientWorkflowCheckpointError(
			"questionnaire_mismatch",
			"Questionnaire template identity or version does not match",
		)
	}
	if (typeof value.journeyDefinitionId !== "string" || value.journeyDefinitionId.trim().length === 0) {
		throw new PatientWorkflowCheckpointError("invalid_checkpoint", "Journey definition ID is required")
	}
	if (typeof value.value !== "string" || !isPatientWorkflowStateValue(value.value)) {
		throw new PatientWorkflowCheckpointError("invalid_checkpoint", "Workflow state value is invalid")
	}
	if (!machine.stateValues.has(value.value)) {
		throw new PatientWorkflowCheckpointError(
			"template_mismatch",
			`Workflow template does not contain state ${value.value}`,
		)
	}
	const context = parseCheckpointContext(value.context)
	const revision = assertNonNegativeInteger(value.revision, "revision")
	assertStateContextInvariant(machine, value.value, context)
	return checkpointFromState({
		machine,
		journeyDefinitionId: value.journeyDefinitionId,
		value: value.value,
		context,
		revision,
	})
}

function createRestoredActor(machine: PatientOrderMachine, checkpoint: PatientWorkflowCheckpoint) {
	const resolvedState = machine.machine.resolveState({
		value: checkpoint.value,
		context: checkpoint.context,
	})
	const actor = createActor(machine.machine, { snapshot: resolvedState })
	actor.start()
	return actor
}

export function transitionPatientWorkflowCheckpoint(
	machine: PatientOrderMachine,
	args: {
		readonly checkpoint: PatientWorkflowCheckpoint
		readonly event: PatientWorkflowEvent
		readonly actor: Actor
		readonly revision: number
	},
): PatientWorkflowTransitionResult {
	const checkpoint = restorePatientWorkflowCheckpoint(machine, args.checkpoint)
	if (!isPatientWorkflowEventType(args.event.type)) {
		return {
			ok: false,
			error: { type: "unsupported_event", eventType: args.event.type },
		}
	}
	if (!canActorSendPatientWorkflowEvent(args.actor, args.event.type)) {
		return {
			ok: false,
			error: { type: "unauthorized_actor", eventType: args.event.type },
		}
	}
	const expectedRevision = checkpoint.revision + 1
	if (args.revision !== expectedRevision) {
		return {
			ok: false,
			error: {
				type: "invalid_revision",
				expected: expectedRevision,
				received: args.revision,
			},
		}
	}
	const actor = createRestoredActor(machine, checkpoint)
	const before = actor.getSnapshot()
	if (!before.can(args.event)) {
		actor.stop()
		return {
			ok: false,
			error: {
				type: "invalid_transition",
				fromState: checkpoint.value,
				eventType: args.event.type,
			},
		}
	}
	actor.send(args.event)
	const after = actor.getSnapshot()
	const nextValue = getActorStateValue(after.value)
	const nextContext = after.context
	actor.stop()
	return {
		ok: true,
		checkpoint: checkpointFromState({
			machine,
			journeyDefinitionId: checkpoint.journeyDefinitionId,
			value: nextValue,
			context: nextContext,
			revision: args.revision,
		}),
	}
}

function getStateMetadata(
	machine: PatientOrderMachine,
	checkpoint: PatientWorkflowCheckpoint,
): PatientWorkflowStateMetadata {
	const metadata = machine.stateMetadata[checkpoint.value]
	if (metadata === undefined) {
		throw new PatientWorkflowCheckpointError("template_mismatch", `Workflow state ${checkpoint.value} has no metadata`)
	}
	if (checkpoint.context.kitLabUploadPayment !== undefined && metadata.editable) {
		return { ...metadata, editable: false }
	}
	if (
		checkpoint.value === "PAYMENT_APPROVED" &&
		isPaymentAfterLab(machine.workflowTemplate, checkpoint.context) &&
		!completesClinicalReviewDuringIntake(machine.workflowTemplate)
	) {
		return {
			...metadata,
			canonicalStep: "clinical-review-questionnaire",
			progressPercent: 88,
		}
	}
	if (checkpoint.value === "LAB_FAILED" && checkpoint.context.labFulfillmentMethod === "ship_kit") {
		return { ...metadata, canonicalStep: "next-steps" }
	}
	if (
		(checkpoint.value === "LAB_RESULTS_REJECTED" || checkpoint.value === "LAB_REORDER_REQUIRED") &&
		checkpoint.context.labFulfillmentMethod === "upload_existing_results"
	) {
		return { ...metadata, canonicalStep: "lab-results" }
	}
	return metadata
}

function getConfiguredIntakePrefix(
	machine: PatientOrderMachine,
	canonicalStep: PatientStepId,
): readonly PatientStepId[] {
	const index = machine.workflowTemplate.intakeSteps.indexOf(canonicalStep)
	if (index < 0) return [canonicalStep]
	return machine.workflowTemplate.intakeSteps.slice(0, index + 1)
}

export function getCanonicalPatientStep(
	machine: PatientOrderMachine,
	checkpointValue: PatientWorkflowCheckpoint,
): PatientStepId | null {
	const checkpoint = restorePatientWorkflowCheckpoint(machine, checkpointValue)
	return getStateMetadata(machine, checkpoint).canonicalStep
}

export function getProjectedOrderStatus(
	machine: PatientOrderMachine,
	checkpointValue: PatientWorkflowCheckpoint,
): OrderStatus {
	const checkpoint = restorePatientWorkflowCheckpoint(machine, checkpointValue)
	return getStateMetadata(machine, checkpoint).currentStatus
}

export function isPatientWorkflowReadyForProductOrder(
	machine: PatientOrderMachine,
	checkpointValue: PatientWorkflowCheckpoint,
): boolean {
	const checkpoint = restorePatientWorkflowCheckpoint(machine, checkpointValue)
	if (patientWorkflowProductOrderReadyStates.has(checkpoint.value)) {
		return true
	}
	return checkpoint.value === "PAYMENT_APPROVED" && isPaymentAfterLab(machine.workflowTemplate, checkpoint.context)
}

export function getAccessiblePatientSteps(
	machine: PatientOrderMachine,
	checkpointValue: PatientWorkflowCheckpoint,
): readonly PatientStepId[] {
	const checkpoint = restorePatientWorkflowCheckpoint(machine, checkpointValue)
	const metadata = getStateMetadata(machine, checkpoint)
	const canonicalStep = metadata.canonicalStep
	if (canonicalStep === null) return []
	if (checkpoint.value === "account") return ["questionnaire", "account"]
	if (checkpoint.value === "ELIGIBILITY_FAILED") {
		return ["eligibility-failed"]
	}
	if (!metadata.editable) return [canonicalStep]

	const intakePrefix = getConfiguredIntakePrefix(machine, canonicalStep).filter((step) => step !== "account")
	if (canonicalStep === "payment") {
		return [...machine.workflowTemplate.intakeSteps.filter((step) => step !== "account"), "payment"]
	}
	if (checkpoint.value === "lab-upload") {
		return machine.workflowTemplate.intakeSteps.filter((step) => step !== "account")
	}
	if (checkpoint.value === "LAB_REQUIRED") {
		return [...machine.workflowTemplate.intakeSteps.filter((step) => step !== "account"), "next-steps"]
	}
	return intakePrefix
}

export function canAccessPatientStep(
	machine: PatientOrderMachine,
	checkpoint: PatientWorkflowCheckpoint,
	requestedStep: PatientStepId,
): boolean {
	return getAccessiblePatientSteps(machine, checkpoint).includes(requestedStep)
}

function getPreviousEditablePatientStep(
	machine: PatientOrderMachine,
	checkpoint: PatientWorkflowCheckpoint,
): PatientStepId | null {
	const canonicalStep = getStateMetadata(machine, checkpoint).canonicalStep
	if (canonicalStep === null) return null
	const accessibleSteps = getAccessiblePatientSteps(machine, checkpoint)
	const canonicalIndex = accessibleSteps.lastIndexOf(canonicalStep)
	if (canonicalIndex <= 0) return null
	return accessibleSteps[canonicalIndex - 1] ?? null
}

function eventProbe(eventType: PatientWorkflowEventType): PatientWorkflowEvent {
	if (eventType === "lab.switch_to_upload") return { type: eventType, payment: "unpaid" }
	if (eventType === "lab.fulfillment_selected") {
		return { type: eventType, labFulfillmentMethod: "ship_kit" }
	}
	return { type: eventType }
}

export function getPatientWorkflowCommands(
	machine: PatientOrderMachine,
	checkpointValue: PatientWorkflowCheckpoint,
	actorIdentity: Actor,
): readonly PatientWorkflowEventType[] {
	const checkpoint = restorePatientWorkflowCheckpoint(machine, checkpointValue)
	const actor = createRestoredActor(machine, checkpoint)
	const snapshot = actor.getSnapshot()
	const commands = PATIENT_WORKFLOW_EVENT_TYPES.filter(
		(eventType) => canActorSendPatientWorkflowEvent(actorIdentity, eventType) && snapshot.can(eventProbe(eventType)),
	)
	actor.stop()
	return commands
}

export function getPatientWorkflowView(
	machine: PatientOrderMachine,
	checkpointValue: PatientWorkflowCheckpoint,
	actor: Actor,
): PatientWorkflowView {
	const checkpoint = restorePatientWorkflowCheckpoint(machine, checkpointValue)
	const metadata = getStateMetadata(machine, checkpoint)
	return {
		stateValue: checkpoint.value,
		currentStatus: metadata.currentStatus,
		canonicalStep: metadata.canonicalStep,
		accessibleSteps: getAccessiblePatientSteps(machine, checkpoint),
		previousEditableStep: getPreviousEditablePatientStep(machine, checkpoint),
		progressPercent: metadata.progressPercent,
		editable: metadata.editable,
		allowedEvents: getPatientWorkflowCommands(machine, checkpoint, actor),
		revision: checkpoint.revision,
	}
}
export type PatientQuestionnaireTemplate = {
	readonly id: string
	readonly version: string
	readonly questions: readonly { readonly phase: QuestionnairePhase }[]
}

export const patientWorkflow = {
	machineId: PATIENT_ORDER_WORKFLOW_MACHINE_ID,
	machineVersion: PATIENT_ORDER_WORKFLOW_MACHINE_VERSION,
	stateValues: PATIENT_WORKFLOW_STATE_VALUES,
	eventTypes: PATIENT_WORKFLOW_EVENT_TYPES,
	terminalOrderStatuses: PATIENT_WORKFLOW_TERMINAL_ORDER_STATUSES,
	actorPolicy: PATIENT_WORKFLOW_ACTOR_POLICY,
	CheckpointError: PatientWorkflowCheckpointError,
	TemplateError: PatientWorkflowTemplateError,
	canAccessStep: canAccessPatientStep,
	canActorSend: canActorSendPatientWorkflowEvent,
	createCheckpoint: createPatientWorkflowCheckpoint,
	checkpointFromSnapshot: createPatientWorkflowCheckpointFromSnapshot,
	createPersistedCheckpoint: createPersistedPatientWorkflowCheckpoint,
	canonicalStep: getCanonicalPatientStep,
	commands: getPatientWorkflowCommands,
	view: getPatientWorkflowView,
	projectedStatus: getProjectedOrderStatus,
	hasVisitHistory: hasPatientWorkflowVisitHistory,
	isEventType: isPatientWorkflowEventType,
	isReadyForProductOrder: isPatientWorkflowReadyForProductOrder,
	restore: restorePatientWorkflowCheckpoint,
	transition: transitionPatientWorkflowCheckpoint,
} as const
